/**
 * TopologyDesignCanvas — the design-mode builder. Unlike the read-only
 * TopologyCanvas (which renders a persisted topology), this is bound to
 * `useCanvasStore` as the single source of truth: drag agents in from the
 * palette, connect them with typed edges, and the CanvasToolbar saves the
 * result as a real topology.
 */

import { useCallback, useEffect } from "react";
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  applyNodeChanges,
  applyEdgeChanges,
  useReactFlow,
  BackgroundVariant,
  type Node,
  type Edge,
  type NodeChange,
  type EdgeChange,
  type OnConnect,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { MousePointer2 } from "lucide-react";
import { AgentNode } from "./AgentNode.js";
import { CustomEdge } from "./EdgeComponent.js";
import { CanvasToolbar } from "./CanvasToolbar.js";
import { useCanvasStore, createAgentNode } from "../../stores/canvas.js";
import { useWorldStore } from "../../stores/world.js";
import { useTopology } from "../../api/hooks.js";
import { topologyToBuilder } from "../../lib/topology-to-builder.js";

const nodeTypes = { agent: AgentNode };
const edgeTypes = { custom: CustomEdge };
let edgeSeq = 0;

export function TopologyDesignCanvas() {
  const nodes = useCanvasStore((s) => s.nodes);
  const edges = useCanvasStore((s) => s.edges);
  const setNodes = useCanvasStore((s) => s.setNodes);
  const setEdges = useCanvasStore((s) => s.setEdges);
  const addNode = useCanvasStore((s) => s.addNode);
  const setEntryPoint = useCanvasStore((s) => s.setEntryPoint);
  const selectNode = useCanvasStore((s) => s.selectNode);
  const { screenToFlowPosition } = useReactFlow();

  // Load the selected topology into the builder so "Design" edits the topology
  // you're looking at (vs. a stale/blank canvas). "+ New → Blank" deselects and
  // resets (loadedTopologyId → null), giving a fresh build. Seeding is keyed off
  // the store's loadedTopologyId so edits survive monitor↔design toggles, but a
  // different selected topology re-seeds.
  const selectedTopologyId = useWorldStore((s) => s.selectedTopologyId);
  const { data: topology } = useTopology(selectedTopologyId ?? "");
  const loadedTopologyId = useCanvasStore((s) => s.loadedTopologyId);
  const setLoadedTopologyId = useCanvasStore((s) => s.setLoadedTopologyId);

  useEffect(() => {
    if (selectedTopologyId && topology?.definition) {
      if (loadedTopologyId !== selectedTopologyId) {
        const seeded = topologyToBuilder(topology.definition);
        setNodes(seeded.nodes);
        setEdges(seeded.edges);
        selectNode(null);
        setLoadedTopologyId(selectedTopologyId);
      }
    } else if (!selectedTopologyId && loadedTopologyId !== null) {
      // Nothing selected (e.g. "+ New → Blank") — show a fresh, empty builder.
      setNodes([]);
      setEdges([]);
      selectNode(null);
      setLoadedTopologyId(null);
    }
  }, [selectedTopologyId, topology, loadedTopologyId, setNodes, setEdges, selectNode, setLoadedTopologyId]);

  const onNodesChange = useCallback(
    (changes: NodeChange[]) => setNodes(applyNodeChanges(changes, useCanvasStore.getState().nodes)),
    [setNodes],
  );

  const onEdgesChange = useCallback(
    (changes: EdgeChange[]) => setEdges(applyEdgeChanges(changes, useCanvasStore.getState().edges)),
    [setEdges],
  );

  const onConnect: OnConnect = useCallback(
    (params) => {
      if (!params.source || !params.target || params.source === params.target) return;
      edgeSeq += 1;
      const edgeType = useCanvasStore.getState().defaultEdgeType;
      const next: Edge = {
        id: `edge-${edgeSeq}`,
        source: params.source,
        target: params.target,
        type: "custom",
        data: { edgeType, bidirectional: false },
      };
      setEdges([...useCanvasStore.getState().edges, next]);
    },
    [setEdges],
  );

  const onDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  }, []);

  const onDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      const raw = e.dataTransfer.getData("application/agent-world-template");
      if (!raw) return;
      let tpl: { backend: string; model?: string; label?: string };
      try {
        tpl = JSON.parse(raw);
      } catch {
        return;
      }
      const position = screenToFlowPosition({ x: e.clientX, y: e.clientY });
      const node = createAgentNode({
        backend: tpl.backend,
        model: tpl.model,
        label: tpl.label,
        position,
      });
      addNode(node);
      // The first agent placed becomes the entry point by default.
      const hasEntry = useCanvasStore.getState().nodes.some(
        (n) => (n.data as { isEntryPoint?: boolean })?.isEntryPoint,
      );
      if (!hasEntry) setEntryPoint(node.id);
      selectNode(node.id);
    },
    [screenToFlowPosition, addNode, setEntryPoint, selectNode],
  );

  const onNodeClick = useCallback(
    (_e: React.MouseEvent, node: Node) => selectNode(node.id),
    [selectNode],
  );
  const onPaneClick = useCallback(() => selectNode(null), [selectNode]);

  return (
    <div className="relative h-full w-full" onDrop={onDrop} onDragOver={onDragOver}>
      <CanvasToolbar />
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onNodeClick={onNodeClick}
        onPaneClick={onPaneClick}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        nodesDraggable
        nodesConnectable
        fitView
        proOptions={{ hideAttribution: true }}
      >
        <Background variant={BackgroundVariant.Dots} gap={24} size={1} color="#27272a" />
        <Controls className="!bg-surface-light/80 !backdrop-blur-sm !border-border-subtle/20 !rounded-lg" />
        <MiniMap
          nodeColor="#3f3f46"
          maskColor="rgba(10, 10, 11, 0.8)"
          className="!bg-surface-light/80 !backdrop-blur-sm !border-border-subtle/20 !rounded-lg"
        />
      </ReactFlow>

      {nodes.length === 0 && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <div className="text-center">
            <MousePointer2 className="mx-auto mb-3 h-7 w-7 text-text-muted/50" />
            <p className="text-sm text-text-secondary">Drag an agent from the palette to start.</p>
            <p className="mt-1 text-xs text-text-muted">
              Connect them, mark an entry &amp; exit, then Save.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
